import React, { useMemo, useState } from 'react';

import { Box, Tooltip } from '@mui/material';

import { HEIGHT, MARGIN_BOTTOM, styles } from './expandableStyles';

interface IProps {
  label: string;
  titleKeyType: 'name' | 'title';
  items: {
    id: number;
    name?: string;
    title?: string;
    url: string;
  }[];
  maxItems?: number;
  maxItemsLink?: string;
}

const Tag = ({
  item,
  titleKeyType,
  showTooltip,
  setShowTooltip,
  handleShowTooltip,
  badge
}: {
  item: IProps['items'][0];
  titleKeyType: IProps['titleKeyType'];
  showTooltip: boolean;
  setShowTooltip: React.Dispatch<React.SetStateAction<boolean>>;
  handleShowTooltip: (event: React.MouseEvent<HTMLDivElement>) => void;
  badge?: {
    show: boolean;
    handleExpandClick: () => void;
    numberOfItems: number;
  };
}) => {
  const badgeFontSize = (numberOfItems: number) => {
    if (numberOfItems < 10) {
      return '14px';
    }
    if (numberOfItems < 100) {
      return '12px';
    }
    return '10px';
  };
  return (
    <>
      <Tooltip
        key={item.id}
        disableFocusListener={!showTooltip}
        disableHoverListener={!showTooltip}
        placement="top"
        title={item[titleKeyType as keyof typeof item]}
      >
        <Box
          key={item.id}
          id="job-name-tag-button"
          onAuxClick={() => item.url && window.open(item.url, '_blank')}
          onClick={() => item.url && window.open(item.url, '_blank')}
          onMouseEnter={handleShowTooltip}
          onMouseLeave={() => setShowTooltip(false)}
          sx={{
            ...styles.item,
            ...{ width: badge?.show ? '82.5%' : '100%' },
            ...(item.url && { cursor: 'pointer' })
          }}
        >
          {item[titleKeyType as keyof typeof item]}
        </Box>
      </Tooltip>
      {badge?.show ? (
        <Box
          id="show-more-job-tag-button"
          onClick={badge.handleExpandClick}
          sx={{ ...styles.badge, fontSize: badgeFontSize(badge.numberOfItems) }}
        >
          {`+${badge.numberOfItems}`}
        </Box>
      ) : null}
    </>
  );
};

const ExpandableLinkedTags = ({
  label,
  titleKeyType,
  items,
  maxItems,
  maxItemsLink
}: IProps): React.JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandedItems, setShowExpandedItems] = useState(false);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const handleExpandClick = () => {
    if (isExpanded) {
      setIsExpanded(false);
      setTimeout(() => {
        setShowExpandedItems(false);
      }, 500);
    } else {
      setIsExpanded(true);
      setShowExpandedItems(true);
    }
  };

  function getHeight() {
    if (!isExpanded) {
      return `${HEIGHT}px`;
    }
    if (maxItems && items.length > maxItems) {
      return `${(HEIGHT + 3) * (maxItems + 1) + MARGIN_BOTTOM * maxItems}px`;
    }
    return `${HEIGHT * (items.length + 1) + MARGIN_BOTTOM * items.length}px`;
  }

  const handleShowTooltip = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.currentTarget.offsetWidth < event.currentTarget.scrollWidth) {
      setShowTooltip(true);
    }
  };

  const displayedItems = useMemo(
    () => (maxItems ? items.slice(1, maxItems) : items.slice(1)),
    [items, maxItems]
  );

  const followMaxItemsLink = () => {
    if (maxItemsLink) {
      window.open(maxItemsLink, '_self');
    }
  };

  const displayBadge = items.length > 1 && !isExpanded;

  return (
    <Box className={label} sx={{ ...styles.container, ...{ height: getHeight() } }}>
      <Box
        sx={{
          display: 'flex'
        }}
      >
        <Tag
          handleShowTooltip={handleShowTooltip}
          item={items[0]}
          setShowTooltip={setShowTooltip}
          showTooltip={showTooltip}
          titleKeyType={titleKeyType}
          badge={{
            show: displayBadge,
            handleExpandClick,
            numberOfItems: items.length - 1
          }}
        />
      </Box>
      {showExpandedItems
        ? displayedItems.map((item) => (
            <Tag
              key={item.id}
              handleShowTooltip={handleShowTooltip}
              item={item}
              setShowTooltip={setShowTooltip}
              showTooltip={showTooltip}
              titleKeyType={titleKeyType}
            />
          ))
        : null}
      {maxItems && items.length > maxItems ? (
        <Box
          id="show-more-job-tag-button"
          onClick={followMaxItemsLink}
          sx={{ ...styles.item, ...styles.viewAllItem, ...(maxItemsLink && { cursor: 'pointer' }) }}
        >
          {maxItemsLink ? `View all ${items.length}` : `+${items.length - maxItems} more`}
        </Box>
      ) : null}
      <Box
        id="show-less-job-tag-button"
        onClick={handleExpandClick}
        sx={{ ...styles.item, ...styles.showLess }}
      >
        Show less
      </Box>
    </Box>
  );
};

export default ExpandableLinkedTags;
